import './brand.css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from './lexmount-brand.svg';
import logoMark from './lexmount-mark.svg';
import { usePlatformCopy } from './copy';
import { nativeLanguage, platformLanguages } from './languages';
import { LanguagePicker } from './LanguagePicker';
import {
  getAppearance,
  getPlatformTheme,
  getPlatformLanguage,
  requestedLanguage,
  preferenceKeys,
  savePreference,
  subscribePreferences,
  type Appearance,
} from './preferences';

// Assets own their clipping; callers may size the image without revealing the wordmark outside the symbol viewport.
export function BrandArtwork({ full = false }: { readonly full?: boolean }) {
  return (
    <img
      className={full ? 'platform-brand-artwork-full' : 'platform-brand-artwork'}
      src={full ? logo : logoMark}
      width={full ? 1320 : 1100}
      height={full ? 805 : 585}
      alt=""
      aria-hidden="true"
    />
  );
}
export function Brand({ account = false }: { readonly account?: boolean }) {
  const copy = usePlatformCopy();
  return (
    <div className="platform-brand">
      <span className="platform-brand-mark">
        <BrandArtwork />
      </span>
      <span className="platform-brand-name">LEXMOUNT</span>
      {account && <span className="platform-brand-caption">{copy.account}</span>}
    </div>
  );
}
function AppearanceIcon({ appearance }: { readonly appearance: Appearance }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      {appearance === 'light' ? (
        <>
          <circle cx="10" cy="10" r="3.5" />
          <path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.6 3.6 5 5M15 15l1.4 1.4M3.6 16.4 5 15M15 5l1.4-1.4" />
        </>
      ) : appearance === 'dark' ? (
        <path d="M16.8 12.4A7.2 7.2 0 0 1 7.6 3.2a7.3 7.3 0 1 0 9.2 9.2Z" />
      ) : (
        <>
          <rect x="2" y="3" width="16" height="11" rx="2" />
          <path d="M7 18h6M10 14v4" />
        </>
      )}
    </svg>
  );
}
export default function Preferences({
  changeLanguage,
  variant = 'toolbar',
}: {
  readonly changeLanguage: (language: string) => Promise<void>;
  readonly variant?: 'toolbar' | 'settings';
}) {
  const { i18n } = useTranslation();
  const copy = usePlatformCopy();
  const [appearance, setAppearance] = useState(getAppearance);
  const [language, setLanguage] = useState(() => getPlatformLanguage(requestedLanguage()));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    const synchronize = () => {
      setAppearance(getAppearance());
      document.documentElement.style.colorScheme = getPlatformTheme();
      const desired = getPlatformLanguage();
      setLanguage(desired);
      if (i18n.language !== nativeLanguage(desired))
        void changeLanguage(nativeLanguage(desired)).catch(() => {
          if (active) setError(true);
        });
    };
    savePreference(preferenceKeys.language, getPlatformLanguage(requestedLanguage()));
    synchronize();
    const unsubscribe = subscribePreferences(synchronize);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [changeLanguage, i18n]);
  return (
    <div className={`platform-preferences platform-preferences-${variant}`}>
      <div className="platform-language">
        <span className={variant === 'toolbar' ? 'platform-sr-only' : ''}>{copy.language}</span>
        <LanguagePicker
          label={copy.language}
          value={language}
          disabled={pending}
          options={platformLanguages.map(({ tag, label }) => ({ value: tag, label, tag }))}
          onChange={async (value) => {
            setPending(true);
            setError(false);
            try {
              await changeLanguage(nativeLanguage(value));
              if (i18n.language !== nativeLanguage(value)) throw new Error();
              setLanguage(value);
              try {
                sessionStorage.setItem('ui_locales', value);
              } catch {
                /* Storage may be unavailable. */
              }
              const location = new URL(window.location.href);
              if (location.searchParams.has('ui_locales')) {
                location.searchParams.set('ui_locales', value);
                window.history.replaceState(window.history.state, '', location);
              }
              savePreference(preferenceKeys.language, value);
            } catch {
              setError(true);
            } finally {
              setPending(false);
            }
          }}
        />
      </div>
      <div className="platform-appearance-row">
        {variant === 'settings' && <span>{copy.appearance}</span>}
        <div className="platform-appearance" role="group" aria-label={copy.appearance}>
          {(['light', 'dark', 'system'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-label={copy[value]}
              aria-pressed={appearance === value}
              title={copy[value]}
              onClick={() => {
                setAppearance(value);
                savePreference(preferenceKeys.theme, value);
              }}
            >
              <AppearanceIcon appearance={value} />
              {variant === 'settings' && <span>{copy[value]}</span>}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <span role="alert" className="platform-preference-error">
          {copy.languageError}
        </span>
      )}
    </div>
  );
}
